import { VText } from '@vake/ui-kit';
import { FC, useEffect } from 'react';
import { Page } from '../shared/styles/wrappers';

interface IMember {
    header: string;
    email: string;
    role: string;
    text: JSX.Element;
    img: string;
}
const members: IMember[] = [
    {
        header: 'Thomas Stendahl Leira',
        email: 'thomas@vake.ai',
        role: 'Chief Executive Officer',
        text: (
            <VText>
                M.Sc. in Industrial Economics and Technology Management from NTNU. Thomas has nine years of experience
                in product and business development, and strategy and innovation methods, through work in Telenor
                Norway, Telenor Digital and DNV GL. He’s often found ski touring Norway’s incredible mountain and fjord
                scenery.
            </VText>
        ),
        img: 'images/about/Thomas.jpeg',
    },
    {
        header: 'Adrian Tofting',
        email: 'adrian@vake.ai',
        role: 'Chief Product Officer',
        text: (
            <VText>
                M.Sc. in Geomatics and Computer Science from NTNU. Adrian has several years of experience with technical
                development and processing of spatial data. He has worked on service and technology development for both
                Norkart and Iterate. During his studies Adrian was part of a team that built an autonomous flying drone.
            </VText>
        ),
        img: 'images/about/Adrian.jpeg',
    },
    {
        header: 'Lars Henrik Berg-Jensen',
        email: 'lars@vake.ai',
        role: 'Chief Technology Officer',
        text: (
            <VText>
                M.Sc. in Geomatics and Computer Science from NTNU. Lars has great interest and knowledge in gathering,
                processing and creating value from data. Through studies and work in Iterate, Cisco and Norkart, he has
                gained a wide range of experience: in everything from fluid mechanics calculations to development of
                cloud-based machine learning services. He is also an avid football enthusiast.
            </VText>
        ),
        img: 'images/about/Lars.jpeg',
    },
    {
        header: 'Tugba Gunes',
        email: 'tugba@vake.ai',
        role: 'Chief Commercial Officer',
        text: (
            <VText>
                M.Sc in Political Science from the University of Oslo. Tugba has over 7 years of experience in military
                diplomacy, advising in the defence sector and entrepreneurship. She has worked in the Norwegian
                Embassies of Warsaw and Baku, Norwegian Defence Material Agency and founded her own company. She is a
                dancer, former fencer and is passionate about travel.
            </VText>
        ),
        img: 'images/about/tugba.jpeg',
    },
    {
        header: 'Odd Eirik Igland',
        email: 'oddeirik@vake.ai',
        role: 'Chief Information Officer',
        text: (
            <VText>
                M.Sc. in Artifical Intelligence and Computer Science from NTNU. Odd Eirik has cultivated his interests
                within AI and automization throughout his studies and internships in Statnett, Netlight and Birdsview.
                Odd Eirik is an avid mountaineer and former speed skater, a true Norseman.
            </VText>
        ),
        img: 'images/about/Oddeirik.jpeg',
    },
    {
        header: 'Torstein Solberg',
        email: 'torstein@vake.ai',
        role: 'Chief Data Officer',
        text: (
            <VText>
                M.Sc. in Geomatics and Computer Science from NTNU. With internships in MedTech, FinTech and consultancy
                firms, Torstein brings much software development knowledge to the team. At NTNU, Torstein was highly
                engaged in student activities, serving as Leader in his Student Association, among other things.
            </VText>
        ),
        img: 'images/about/Torstein.jpeg',
    },
    {
        header: 'Sindre Hestnes Kaald',
        email: 'sindre@vake.ai',
        role: 'Chief Scientific Officer',
        text: (
            <VText>
                M.Sc. in physics and mathematics from NTNU. As a former researcher at IFE, Sindre had the rare
                experience of working at an operational nuclear reactor. In the summer, he annoys Oslo's drivers on his
                road bike. During winter, he’s heading deep into the woods, collecting miles on two skis.
            </VText>
        ),
        img: 'images/about/Kaald.jpg',
    },
    {
        header: 'Eilef Osvik',
        email: 'eilef.osvik@vake.ai',
        role: 'Data Analyst',
        text: (
            <VText>
                M.Sc in Cybernetics and Robotics from NTNU. Eilef has, through studies, deep-dived into the world of
                hardware and optimization with a specialization in reinforcement learning for drone control. Engagements
                in student activities and internships have helped form this engaging and reliable team player. Eilef
                enjoys playing music with friends and handling the guitar and piano well.
            </VText>
        ),
        img: 'images/about/eilef.jpg',
    },
    {
        header: 'Andrew Farabow',
        email: 'andrew@vake.ai',
        role: 'Data Analyst',
        text: (
            <VText>
                B.S. in Computer Science from Virginia Tech. Andrew has worked on a diverse range of machine learning
                projects through internships (including a summer at Vake) and research work at Virginia Tech. In his
                free time, he enjoys cooking, brewing beer, and going to the gym.
            </VText>
        ),
        img: 'images/about/placeholder.jpg',
    },
];

const AboutUs: FC = () => {
    useEffect(() => {
        document.title = 'About us';
    }, []);
    return (
        <Page>
            <div className="flex flex-col gap-2 pt-32">
                <div className="flex">
                    <div className="flex flex-col gap-2 w-4/5">
                        <VText variant="Di2">About us</VText>
                        <VText>
                            Vake is a Norwegian machine learning company at the cutting edge of satellite guided
                            Maritime Domain Awareness. We differ from other satellite data analytics companies with our
                            pure maritime focus, a focus that makes us uniquely positioned to extract and provide deep
                            insights across multiple maritime data sources.
                        </VText>
                        <VText>
                            Illegal fishing, assets protection and environmental crimes are immense global challenges.
                            The breakdown of our amphibious ecosystems will affect us for generations to come. Total
                            Maritime Domain Awareness is an important step towards resolving these problems. This is a
                            combined effort, and we want to do our part by providing transparency and accountability for
                            any activity at sea. Are you working towards the same goal?{' '}
                            <a href="mailto:connect@vake.ai">Reach out</a> – we would love to discuss potential
                            partnerships.
                        </VText>
                        <VText variant="H2">Our story</VText>
                        <VText>
                            Vake has been working with satellite imagery and vessel traffic since 2017. The
                            technological foundation for Vake was laid during the two technical founders’ award winning
                            master thesis:{' '}
                            <i>A Big Data Approach to Generate Training Data for Automatic Ship Detection.</i>
                            The theoretical effort has since evolved into a groundbreaking company, processing an
                            extensive volume of satellite imagery on a daily basis. Vake is a highly scalable data-first
                            company, but our team is lean and flexible in accommodating customers' needs.
                        </VText>
                    </div>
                </div>
                <VText variant="H2">Our team</VText>
                <div className="flex flex-wrap gap-4">
                    {members.map((f, i) => (
                        <div key={i} className="flex flex-col gap-2 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                            <div className="flex gap-2">
                                <img className="w-24 h-24 object-cover rounded" src={f.img} alt={`${f.role}`} />
                                <div>
                                    <VText variant="B1B">{f.header}</VText>
                                    <VText significance="secondary">{f.email}</VText>
                                    <VText significance="secondary">{f.role}</VText>
                                </div>
                            </div>
                            <VText>{f.text}</VText>
                        </div>
                    ))}
                </div>
            </div>
        </Page>
    );
};

export default AboutUs;
